import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { testimonials } from '../../data/mock';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ScrollReveal } from '../ui/ScrollReveal';

const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1));
  }, []);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1));
  }, []);

  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(goToNext, 5000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, goToNext]);

  return (
    <section id="testimonials" className="py-12 sm:py-16 md:py-20 bg-[#1A1A1A] relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <motion.div
          className="absolute top-0 left-0 w-96 h-96 bg-[#8B7355] rounded-full blur-3xl"
          animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute bottom-0 right-0 w-96 h-96 bg-[#8B7355] rounded-full blur-3xl"
          animate={{ x: [0, -30, 0], y: [0, 20, 0] }}
          transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut' }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollReveal variant="fadeUp" threshold={0.2}>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 sm:mb-10 md:mb-12">
            <div>
              <span className="text-[#8B7355] text-xs sm:text-sm font-semibold tracking-widest uppercase">Testimonials</span>
              <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white mt-2 sm:mt-3">
                What our clients say
              </h2>
            </div>
            <Link
              to="/clients"
              className="text-[#8B7355] text-xs sm:text-sm font-medium hover:text-[#9A8468] transition-colors"
            >
              View all testimonials →
            </Link>
          </div>
        </ScrollReveal>

        <ScrollReveal variant="fadeUp" threshold={0.1} delay={0.15}>
          <div
            className="relative"
            onMouseEnter={() => setIsAutoPlaying(false)}
            onMouseLeave={() => setIsAutoPlaying(true)}
          >
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 sm:gap-6 lg:gap-8">
              {/* Large Featured Testimonial */}
              <div className="lg:col-span-3 bg-white/5 backdrop-blur-sm border border-white/10 p-6 sm:p-8 md:p-10 rounded-lg">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentIndex}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.4 }}
                  >
                    <div className="flex gap-1 mb-3 sm:mb-4">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 sm:w-5 sm:h-5 fill-[#8B7355] text-[#8B7355]" />
                      ))}
                    </div>
                    <blockquote className="text-white text-base sm:text-lg md:text-xl leading-relaxed mb-4 sm:mb-6 font-light">
                      "{testimonials[currentIndex].quote}"
                    </blockquote>
                    <div className="flex items-center gap-3 sm:gap-4">
                      <div>
                        <h4 className="font-medium text-white text-sm sm:text-base">{testimonials[currentIndex].name}</h4>
                        <p className="text-[#8B7355] text-xs sm:text-sm">{testimonials[currentIndex].role}</p>
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Side testimonials preview */}
              <div className="lg:col-span-2 flex flex-col gap-2 sm:gap-3">
                {testimonials.slice(0, 3).map((testimonial, index) => (
                  <motion.button
                    key={testimonial.id}
                    onClick={() => setCurrentIndex(index)}
                    className={`text-left p-3 sm:p-4 md:p-5 border transition-all duration-300 rounded-lg ${
                      currentIndex === index
                        ? 'bg-[#8B7355]/20 border-[#8B7355]'
                        : 'bg-white/5 border-white/10 hover:border-white/20'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <p className="text-white/70 text-xs sm:text-sm line-clamp-2 mb-2 sm:mb-3">
                      "{testimonial.quote.substring(0, 80)}..."
                    </p>
                    <div className="flex items-center gap-2 sm:gap-3">
                      <span className="text-white/90 text-xs sm:text-sm truncate">{testimonial.name}</span>
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Navigation */}
            <div className="flex items-center gap-3 sm:gap-4 mt-4 sm:mt-6">
              <motion.button
                onClick={goToPrevious}
                className="w-10 h-10 sm:w-12 sm:h-12 border border-white/20 flex items-center justify-center text-white hover:bg-[#8B7355] hover:border-[#8B7355] transition-all"
                aria-label="Previous"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
              </motion.button>
              <motion.button
                onClick={goToNext}
                className="w-10 h-10 sm:w-12 sm:h-12 border border-white/20 flex items-center justify-center text-white hover:bg-[#8B7355] hover:border-[#8B7355] transition-all"
                aria-label="Next"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
              </motion.button>

              <div className="flex gap-1.5 sm:gap-2 ml-2 sm:ml-4">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={`h-1 transition-all duration-300 rounded-full ${
                      currentIndex === index ? 'w-6 sm:w-8 bg-[#8B7355]' : 'w-1.5 sm:w-2 bg-white/30 hover:bg-white/50'
                    }`}
                    aria-label={`Go to testimonial ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default TestimonialsSection;
